import React, { useMemo } from 'react'

import { ErrorMessage, Field, Form, Formik } from 'formik'
import _startCase from 'lodash/startCase'
import reactSelect from 'react-select'
import * as yup from 'yup'

import { useFileManager } from './useFileManager'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { setActiveModal } from '@/features/FileManager/fileManagerSlice'
import { useUpdateUserDocumentMutation } from '@/services/documents'

export function FileRenameModal() {
  const dispatch = useAppDispatch()
  const file = useAppSelector((state) => state.fileManager.activeModal.file)
  const { fileRenameRegex, documentTemplatesLoaded, calculateSuggestedGroupedOptions } =
    useFileManager()
  const [updateDoc] = useUpdateUserDocumentMutation()

  const validationSchema = yup.object().shape({
    name: yup.string().required('Required'),
    date: yup.string().required('Required'),
    identifier: yup
      .string()
      .max(254, 'Max 254 characters')
      .matches(fileRenameRegex, 'Cannot start or end with whitespace'),
  })

  const handleFileRename = async (updatedFile) => {
    const { category, name, identifier, date } = updatedFile
    const fileExt = file.fileName.split('.').pop()
    const newName = identifier
      ? `${category} - ${name} - ${identifier} - ${date}.${fileExt}`
      : `${category} - ${name} - ${date}.${fileExt}`
    await updateDoc({ uuid: file.uuid, original_filename: newName })
    dispatch(setActiveModal(null))
  }

  // Try to infer identifier from filename
  const inferedIdentifier =
    file.fileName?.split(' - ').length === 4 ? file.fileName.split(' - ')[2] : ''

  const groupedOptions = useMemo(
    () => calculateSuggestedGroupedOptions([file]),
    [documentTemplatesLoaded]
  )

  return (
    <Formik
      initialValues={{
        name: '',
        category: '',
        identifier: inferedIdentifier ?? '',
        date: new Date().toISOString().split('T')[0],
      }}
      validationSchema={validationSchema}
      onSubmit={handleFileRename}
    >
      {({ values, isSubmitting }) => (
        <Form>
          <fieldset>
            <table className='w-full border-separate border-spacing-3'>
              <thead>
                <tr className='text-left text-sm'>
                  <th className='font-light'>Original Document Name</th>
                  <th className='font-light'>Document *</th>
                  <th className='font-light'>Identifier (optional)</th>
                  <th className='font-light'>Date *</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className='font-semibold overflow-hidden text-ellipsis whitespace-nowrap'>
                    {file.fileName}
                  </td>
                  <td className='w-96'>
                    <div className='flex gap-3 items-center'>
                      <Field
                        name='name'
                        className='w-full'
                        component={reactSelect}
                        isClearable
                        defaultValue={() => {
                          const suggestedOption = groupedOptions[0][0]?.options[0]
                          values.name = suggestedOption?.label ?? ''
                          values.tag = suggestedOption?.tag ?? ''
                          values.category = _startCase(suggestedOption?.category ?? '')
                          return suggestedOption
                        }}
                        options={groupedOptions[0]}
                        onChange={(doc) => {
                          values.name = doc?.label ?? ''
                          values.category = _startCase(doc?.category ?? '')
                        }}
                      />
                    </div>
                  </td>
                  <td className='w-36'>
                    <Field
                      name='identifier'
                      className='form-input border-gray-300 rounded-md h-[38px] w-full text-gray-600 text-sm'
                    />
                  </td>
                  <td className='w-36'>
                    <div className='flex gap-3 items-center'>
                      <Field
                        name='date'
                        className='border-gray-300 rounded-md h-[38px] w-full text-gray-600 text-sm'
                        type='date'
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>&nbsp;</td>
                  <td className='text-center'>
                    <ErrorMessage
                      name='name'
                      render={(msg) => <div className='text-red-600'>{msg}</div>}
                    />
                  </td>
                  <td>
                    <ErrorMessage
                      name='identifier'
                      render={(msg) => <div className='text-red-600'>{msg}</div>}
                    />
                  </td>
                  <td className='text-center'>
                    <ErrorMessage
                      name='date'
                      render={(msg) => <div className='text-red-600'>{msg}</div>}
                    />
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td
                    colSpan={5}
                    className='text-right'
                  >
                    <button
                      type='submit'
                      className='bg-blue-500 disabled:bg-blue-200 text-white font-bold py-2 px-5 rounded'
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Rename File'}
                    </button>
                  </td>
                </tr>
              </tfoot>
            </table>
          </fieldset>
        </Form>
      )}
    </Formik>
  )
}
